import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';

// Helper function to calculate date ranges based on time period
const calculateDateRange = (timePeriod) => {
  const today = new Date();
  const endDate = new Date(today);

  let startDate = new Date(today);

  switch (timePeriod) {
    case 'daily':
      // Today only
      startDate = new Date(today);
      break;
    case 'weekly':
      // Last 7 days
      startDate.setDate(today.getDate() - 7);
      break;
    case 'monthly':
      // Last 30 days
      startDate.setDate(today.getDate() - 30);
      break;
    case 'quarterly':
      // Last 90 days
      startDate.setDate(today.getDate() - 90);
      break;
    default:
      // Default to today
      startDate = new Date(today);
  }

  // Format dates as YYYY-MM-DD
  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  return {
    startDate: formatDate(startDate),
    endDate: formatDate(endDate)
  };
};

const WorkReportModal = ({ isOpen, onClose, user: currentUser }) => {
  const navigate = useNavigate();
  // Get user profile to access client_id
  const { userProfile } = useAuth();
  const [mode, setMode] = useState('assistant'); // assistant | report
  const [step, setStep] = useState(1); // 1: Time Period, 2: Job Selection, 3: Report Display
  const [selectedTimePeriod, setSelectedTimePeriod] = useState('');
  const [selectedJob, setSelectedJob] = useState('');
  const [availableJobs, setAvailableJobs] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [assistantMessages, setAssistantMessages] = useState([]);
  const [assistantInput, setAssistantInput] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [pendingCommand, setPendingCommand] = useState(null);
  const [isExecutingCommand, setIsExecutingCommand] = useState(false);
  const recognitionRef = useRef(null);

  const QUICK_COMMANDS = [
    'Generate a market intelligence report for Precious Metals Sales Director in Singapore',
    'Run an autonomous candidate search for Trade Finance Executive in Singapore',
    'Show work report summary',
    'Help',
  ];

  const timePeriods = [
    { value: 'daily', label: 'Today', icon: '📅', description: 'Today\'s activity' },
    { value: 'weekly', label: 'Last 7 Days', icon: '📊', description: 'Past week' },
    { value: 'monthly', label: 'Last 30 Days', icon: '📈', description: 'Past month' },
    { value: 'quarterly', label: 'Last 90 Days', icon: '📋', description: 'Past quarter' }
  ];

  useEffect(() => {
    if (isOpen) {
      setMode('assistant');
      setStep(1);
      setSelectedTimePeriod('');
      setSelectedJob('');
      setReportData(null);
      setError('');
      setInfo('');
      setAssistantInput('');
      setPendingCommand(null);
      setAssistantMessages([
        {
          sender: 'assistant',
          text: 'ALICE Work Report Agent is ready. I can run: report_generation, candidate_search, work_report_summary, autonomous search, help.',
        },
        {
          sender: 'assistant',
          text: 'Use voice or type a command, then confirm execution.',
        },
      ]);
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const summarizeCommand = (command) => {
    if (!command || !command.intent) return 'Could not parse command';
    const p = command.params || {};
    if (command.intent === 'report_generation') {
      return `report_generation · ${p.role_title || 'role not detected'}`;
    }
    if (command.intent === 'candidate_search') {
      return `candidate_search · ${p.role_title || 'role not detected'}`;
    }
    if (command.intent === 'work_report_summary') {
      return 'work_report_summary';
    }
    return command.intent;
  };

  const parseCommand = async (transcript, confidence = 0.95) => {
    if (!currentUser) {
      throw new Error('User not authenticated');
    }
    const token = await currentUser.getIdToken();
    const response = await fetch(`${API_BASE_URL}/api/voice/command/parse`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ source: 'voice', transcript, confidence }),
    });
    const data = await response.json();
    if (!response.ok || !data?.success) {
      throw new Error(data?.error || 'Failed to parse command');
    }
    return data.command;
  };

  const executeCommand = async () => {
    if (!pendingCommand || !currentUser) return;
    setIsExecutingCommand(true);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/voice/command/execute`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          command: pendingCommand,
          confirmed: true,
        }),
      });
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.error || 'Command execution failed');
      }
      setAssistantMessages((prev) => [
        ...prev,
        { sender: 'assistant', text: data.message || 'Command executed successfully.' },
      ]);

      const reportId = data?.result?.report_id;
      const sessionId = data?.result?.session_id;
      if (reportId) {
        setAssistantMessages((prev) => [
          ...prev,
          { sender: 'assistant', text: `Report draft ready: ${reportId}. Opening MIE page...` },
        ]);
        navigate(`/mie-intelligence?report_id=${encodeURIComponent(reportId)}`);
      }
      if (sessionId) {
        setAssistantMessages((prev) => [
          ...prev,
          { sender: 'assistant', text: `Campaign session ready: ${sessionId}. Opening refinement...` },
        ]);
        navigate(`/campaigns/autonomous/${encodeURIComponent(sessionId)}/refine`);
      }
      setPendingCommand(null);
    } catch (err) {
      setAssistantMessages((prev) => [
        ...prev,
        { sender: 'assistant', text: `Execution error: ${err.message}` },
      ]);
    } finally {
      setIsExecutingCommand(false);
    }
  };

  const submitAssistantCommand = async (text) => {
    const input = String(text || '').trim();
    if (!input) return;
    setAssistantMessages((prev) => [...prev, { sender: 'user', text: input }]);
    setAssistantInput('');
    try {
      const command = await parseCommand(input, 0.95);
      setPendingCommand(command);
      setAssistantMessages((prev) => [
        ...prev,
        { sender: 'assistant', text: `Parsed: ${summarizeCommand(command)}. Confirm to execute.` },
      ]);
    } catch (err) {
      setAssistantMessages((prev) => [
        ...prev,
        { sender: 'assistant', text: `Parse error: ${err.message}` },
      ]);
    }
  };

  const toggleVoiceInput = () => {
    if (!('webkitSpeechRecognition' in window)) {
      setAssistantMessages((prev) => [
        ...prev,
        { sender: 'assistant', text: 'Voice input is not supported in this browser.' },
      ]);
      return;
    }
    if (!recognitionRef.current) {
      recognitionRef.current = new window.webkitSpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript || '';
        submitAssistantCommand(transcript);
      };
      recognitionRef.current.onend = () => setIsListening(false);
      recognitionRef.current.onerror = () => setIsListening(false);
    }

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  const handleTimePeriodSelect = async (period) => {
    setSelectedTimePeriod(period);
    setIsLoading(true);
    setError('');
    setInfo('');

    try {
      if (!currentUser) {
        setError('User not authenticated');
        return;
      }

      if (!userProfile?.client_id) {
        setError(
          'Your account is missing a client profile (client_id). Ask an administrator to link your user to a client, then sign out and back in.'
        );
        return;
      }

      const token = await currentUser.getIdToken();

      if (!token) {
        setError('Authentication token not available');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/work-report/instant-status?time_period=${period}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.error) {
          setError(typeof data.error === 'string' ? data.error : 'Failed to load jobs');
          return;
        }
        setAvailableJobs(data.active_jobs || []);
        if ((data.active_jobs || []).length === 0 && data.message) {
          setInfo(data.message);
        }
        setStep(2);
      } else {
        let msg = `Failed to fetch available jobs (${response.status})`;
        try {
          const errBody = await response.json();
          if (errBody.error || errBody.message) {
            msg = errBody.error || errBody.message;
          }
        } catch {
          /* use default */
        }
        setError(msg);
      }
    } catch (error) {
      console.error('Work Report Modal: Error fetching jobs:', error);
      setError(`Error fetching jobs: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleJobSelect = async (job) => {
    setSelectedJob(job);
    setIsLoading(true);
    setError('');
    setInfo('');

    try {
      if (!currentUser) {
        setError('User not authenticated');
        return;
      }

      if (!userProfile?.client_id) {
        setError(
          'Your account is missing a client profile (client_id). Ask an administrator to link your user to a client, then sign out and back in.'
        );
        return;
      }

      const token = await currentUser.getIdToken();
      if (!token) {
        setError('Authentication token not available');
        return;
      }

      // Calculate date range based on selected time period
      const { startDate, endDate } = calculateDateRange(selectedTimePeriod);

      const response = await fetch(`${API_BASE_URL}/api/work-report/summary?start_date=${startDate}&end_date=${endDate}&time_period=${selectedTimePeriod}&job_id=${job}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status === 'error' && data.error) {
          setError(typeof data.error === 'string' ? data.error : 'Report failed');
          return;
        }
        setReportData(data);
        setStep(3);
      } else {
        let msg = `Failed to generate report (${response.status})`;
        try {
          const errBody = await response.json();
          if (errBody.error || errBody.message) {
            msg = errBody.error || errBody.message;
          }
        } catch {
          /* use default */
        }
        setError(msg);
      }
    } catch (error) {
      console.error('Work Report Modal: Error generating report:', error);
      setError(`Error generating report: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
      setSelectedJob('');
      setAvailableJobs([]);
    } else if (step === 3) {
      setStep(2);
      setReportData(null);
    }
  };

  const handleClose = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" onClick={handleClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-11/12 max-w-2xl max-h-[80vh] overflow-hidden animate-in slide-in-from-bottom-4 duration-300" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-amber-900/20 bg-gradient-to-r from-amber-600 via-orange-600 to-amber-700 text-white">
          <h2 className="text-2xl font-semibold m-0">📊 Work Report Agent</h2>
          <button
            className="bg-transparent border-none text-white text-2xl cursor-pointer p-2 rounded-lg transition-colors hover:bg-white/10"
            onClick={handleClose}
          >
            ✕
          </button>
        </div>

        <div className="p-8 max-h-[60vh] overflow-y-auto">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 p-3 rounded-lg mb-6 text-sm">
              {error}
            </div>
          )}
          {info && !error && (
            <div className="bg-amber-50 border border-amber-200 text-amber-900 p-3 rounded-lg mb-6 text-sm">
              {info}
            </div>
          )}

          <div className="flex items-center gap-2 mb-6">
            <button
              type="button"
              onClick={() => setMode('assistant')}
              className={`px-3 py-2 rounded-lg text-sm font-semibold border ${mode === 'assistant' ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-gray-700 border-gray-300'}`}
            >
              Agent Conversation
            </button>
            <button
              type="button"
              onClick={() => setMode('report')}
              className={`px-3 py-2 rounded-lg text-sm font-semibold border ${mode === 'report' ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-gray-700 border-gray-300'}`}
            >
              Work Report Wizard
            </button>
          </div>

          {mode === 'assistant' && (
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Command ALICE</h3>
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4 max-h-72 overflow-y-auto">
                {assistantMessages.map((m, idx) => (
                  <div key={`${m.sender}-${idx}`} className={`mb-2 ${m.sender === 'user' ? 'text-right' : 'text-left'}`}>
                    <span className={`inline-block px-3 py-2 rounded-lg text-sm ${m.sender === 'user' ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-800'}`}>
                      {m.text}
                    </span>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                {QUICK_COMMANDS.map((cmd) => (
                  <button
                    key={cmd}
                    type="button"
                    onClick={() => submitAssistantCommand(cmd)}
                    className="px-3 py-1.5 rounded-full text-xs font-medium bg-white border border-gray-300 text-gray-700 hover:border-amber-500"
                  >
                    {cmd}
                  </button>
                ))}
              </div>

              {pendingCommand && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
                  <div className="text-sm font-semibold text-gray-800 mb-1">Parsed intent</div>
                  <div className="text-xs text-gray-700 mb-2">{summarizeCommand(pendingCommand)}</div>
                  {Array.isArray(pendingCommand.risk_flags) && pendingCommand.risk_flags.length > 0 && (
                    <div className="text-xs text-red-600 mb-2">Risk flags: {pendingCommand.risk_flags.join(', ')}</div>
                  )}
                  <button
                    type="button"
                    onClick={executeCommand}
                    disabled={isExecutingCommand}
                    className="px-3 py-2 rounded-lg text-xs font-semibold bg-green-600 text-white disabled:bg-gray-400"
                  >
                    {isExecutingCommand ? 'Executing...' : 'Confirm & Execute'}
                  </button>
                </div>
              )}

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={toggleVoiceInput}
                  className={`px-3 py-2 rounded-lg text-sm font-semibold border ${isListening ? 'bg-red-600 text-white border-red-600' : 'bg-white text-gray-700 border-gray-300'}`}
                >
                  {isListening ? 'Stop Voice' : 'Start Voice'}
                </button>
                <input
                  type="text"
                  value={assistantInput}
                  onChange={(e) => setAssistantInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      submitAssistantCommand(assistantInput);
                    }
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="Type a command (report_generation, candidate_search, work_report_summary, autonomous search, help)"
                />
                <button
                  type="button"
                  onClick={() => submitAssistantCommand(assistantInput)}
                  className="px-4 py-2 rounded-lg text-sm font-semibold bg-amber-600 text-white"
                >
                  Send
                </button>
              </div>
            </div>
          )}

          {/* Step 1: Time Period Selection */}
          {mode === 'report' && step === 1 && (
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
              <h3 className="text-xl font-semibold text-gray-800 text-center mb-6">What type of report would you like?</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {timePeriods.map((period) => (
                  <button
                    key={period.value}
                    className="flex flex-col items-center p-5 border-2 border-gray-200 rounded-xl bg-white cursor-pointer transition-all duration-200 hover:border-amber-500 hover:scale-105 hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none min-h-[120px]"
                    onClick={() => handleTimePeriodSelect(period.value)}
                    disabled={isLoading}
                  >
                    <span className="text-3xl mb-2">{period.icon}</span>
                    <span className="text-sm font-semibold text-gray-700">{period.label}</span>
                    <span className="text-xs text-gray-500 mt-1 text-center">{period.description}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Job Selection */}
          {mode === 'report' && step === 2 && (
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
              {(() => {
                const { startDate, endDate } = calculateDateRange(selectedTimePeriod);
                const periodLabel = timePeriods.find(p => p.value === selectedTimePeriod)?.label || selectedTimePeriod;
                return (
                  <h3 className="text-xl font-semibold text-gray-800 text-center mb-6">
                    Report: {periodLabel} ({startDate} to {endDate}). For which job?
                  </h3>
                );
              })()}
              <div className="flex flex-col gap-3 mb-6">
                <button
                  className="flex items-center p-4 border-2 border-gray-200 rounded-xl bg-white cursor-pointer transition-all duration-200 hover:border-amber-500 hover:bg-gray-50 hover:translate-x-1 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none text-left"
                  onClick={() => handleJobSelect('all')}
                  disabled={isLoading}
                >
                  <span className="text-xl mr-3">📋</span>
                  <span className="text-base font-medium text-gray-700">All Jobs</span>
                </button>
                {availableJobs.map((job) => (
                  <button
                    key={job.id}
                    className="flex items-center p-4 border-2 border-gray-200 rounded-xl bg-white cursor-pointer transition-all duration-200 hover:border-amber-500 hover:bg-gray-50 hover:translate-x-1 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none text-left"
                    onClick={() => handleJobSelect(job.id)}
                    disabled={isLoading}
                  >
                    <span className="text-xl mr-3">💼</span>
                    <span className="text-base font-medium text-gray-700">{job.title || job.id}</span>
                  </button>
                ))}
              </div>
              <button
                className="bg-transparent border-none text-gray-500 text-sm cursor-pointer px-4 py-2 rounded-md transition-colors hover:bg-gray-100 hover:text-gray-700"
                onClick={handleBack}
              >
                ← Back
              </button>
            </div>
          )}

          {/* Step 3: Report Display */}
          {mode === 'report' && step === 3 && (
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
              <h3 className="text-xl font-semibold text-gray-800 text-center mb-6">{selectedTimePeriod.charAt(0).toUpperCase() + selectedTimePeriod.slice(1)} Report: {selectedJob === 'all' ? 'All Jobs' : selectedJob}</h3>

              {reportData && (
                <div className="mt-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-5 text-center border border-gray-200 transition-transform hover:scale-105">
                      <div className="text-2xl mb-2">📞</div>
                      <div className="text-3xl font-bold text-gray-800 mb-1">{reportData.calls?.total || 0}</div>
                      <div className="text-sm font-semibold text-gray-600 mb-1">Calls Made</div>
                      <div className="text-xs text-gray-500">{(reportData.calls?.response_rate || 0)}% response rate</div>
                    </div>

                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-5 text-center border border-gray-200 transition-transform hover:scale-105">
                      <div className="text-2xl mb-2">📧</div>
                      <div className="text-3xl font-bold text-gray-800 mb-1">{reportData.emails?.total || 0}</div>
                      <div className="text-sm font-semibold text-gray-600 mb-1">Emails Sent</div>
                      <div className="text-xs text-gray-500">{(reportData.emails?.response_rate || 0)}% response rate</div>
                    </div>

                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-5 text-center border border-gray-200 transition-transform hover:scale-105">
                      <div className="text-2xl mb-2">💬</div>
                      <div className="text-3xl font-bold text-gray-800 mb-1">{reportData.whatsapp?.total || 0}</div>
                      <div className="text-sm font-semibold text-gray-600 mb-1">WhatsApp</div>
                      <div className="text-xs text-gray-500">{(reportData.whatsapp?.response_rate || 0)}% response rate</div>
                    </div>

                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-5 text-center border border-gray-200 transition-transform hover:scale-105">
                      <div className="text-2xl mb-2">🎯</div>
                      <div className="text-3xl font-bold text-gray-800 mb-1">{reportData.interviews?.scheduled || 0}</div>
                      <div className="text-sm font-semibold text-gray-600 mb-1">Interviews</div>
                      <div className="text-xs text-gray-500">Scheduled today</div>
                    </div>
                  </div>

                  <div className="flex gap-3 justify-center mb-6">
                    <button
                      type="button"
                      className="px-6 py-3 rounded-lg text-sm font-semibold cursor-default transition-all duration-200 bg-gradient-to-r from-amber-600 to-orange-600 text-white opacity-90"
                      title="Use the metrics above; full export is not wired yet."
                    >
                      📊 Summary (on screen)
                    </button>
                    <button
                      type="button"
                      disabled
                      className="px-6 py-3 rounded-lg text-sm font-semibold cursor-not-allowed opacity-50 bg-white text-gray-500 border-2 border-gray-200"
                      title="PDF export is not implemented yet."
                    >
                      📥 Export PDF
                    </button>
                  </div>
                </div>
              )}

              <button
                className="bg-transparent border-none text-gray-500 text-sm cursor-pointer px-4 py-2 rounded-md transition-colors hover:bg-gray-100 hover:text-gray-700"
                onClick={handleBack}
              >
                ← Back
              </button>
            </div>
          )}

          {isLoading && (
              <div className="absolute inset-0 bg-white/90 flex flex-col items-center justify-center z-10">
              <div className="w-10 h-10 border-4 border-gray-200 border-t-amber-600 rounded-full animate-spin mb-4"></div>
              <p className="text-gray-600">Generating report...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkReportModal;
